/*
 * @Author: rossdong rossdong@yntengyun.com
 * @Date: 2026-03-30 17:41:29
 * @LastEditors: rossdong rossdong@yntengyun.com
 * @LastEditTime: 2026-03-30 17:57:45
 * @FilePath: /Mobile/src/pages/complaint/views/visual/composables/useChart.js
 * @Description: ECharts 图表生命周期管理 Hook
 *
 */
import { ref, watch, nextTick, onBeforeUnmount } from 'vue';
import echarts from 'echarts';

/**
 * 简易 Deferred 实现（替代 p-defer）
 */
function createDeferred() {
  let resolve, reject;
  const promise = new Promise((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
}

/**
 * @param {Function} createOption - 接收 chartInstance，返回 ECharts option 的函数（支持 async）
 * @param {Object} config - 配置项 { showLoading: boolean }
 */
export function useChart(createOption, config) {
  const chartContainer = ref(null);
  let chartInstance = null;
  let deferred = createDeferred();

  // 核心：监听 ref 绑定的 DOM 元素变化
  // 当 v-if 切换导致 DOM 重建时，watcher 会自动触发重新初始化
  watch(chartContainer, (dom, prevDom) => {
    if (dom && dom.clientWidth && dom.clientHeight) {
      nextTick(async () => {
        chartInstance = echarts.init(dom);
        if (config && config.showLoading) {
          chartInstance.showLoading();
        }
        const option = (await (createOption && createOption(chartInstance))) || {};
        if (config && config.showLoading) {
          chartInstance.hideLoading();
        }
        chartInstance.setOption(option);
        deferred.resolve();
      });
    } else if (prevDom && chartInstance) {
      // DOM 被移除（v-if 切换为 false），销毁旧实例
      chartInstance.dispose();
      chartInstance = null;
      deferred = createDeferred();
    }
  });

  // resize 处理
  function chartResize() {
    if (chartInstance) chartInstance.resize();
  }

  let _onResize = () => chartResize();
  window.addEventListener('resize', _onResize, { passive: true });

  /**
   * 重新设置 option（区域切换等场景使用）
   * @param {Function} newCreateOption - 新的 option 构建函数
   */
  async function resetOption(newCreateOption) {
    if (chartInstance) {
      const newOption = (await (newCreateOption && newCreateOption(chartInstance))) || {};
      chartInstance.setOption(newOption, true);
    }
  }

  onBeforeUnmount(() => {
    window.removeEventListener('resize', _onResize);
    if (chartInstance) {
      chartInstance.dispose();
      chartInstance = null;
    }
  });

  return {
    chartContainer,
    resetOption,
    getChartInstance: () => chartInstance
  };
}
